import type { FC, ReactElement } from 'react';
import { useEffect } from 'react';

import { ButtonVariant } from '@pp/core/src/components/controls/button/Button';
import { CurrencyConnected } from '@pp/core/src/components/currency/CurrencyConnected';
import { BlockingModalActionButton } from '@pp/core/src/components/popup/content/BlockingModalContent';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { Logger } from '@pp/core/src/logger/Logger';
import { CoreDispatch, useCoreDispatch, type CoreStoreType } from '@pp/core/src/store/CoreStore';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { BetType } from '@pp/core/src/store/slices/bets/types';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { popupActions } from '@pp/core/src/store/slices/popup/slice';
import { toastActions } from '@pp/core/src/store/slices/toast/slice';

import { useValidBetInputValue } from '../../components/gameGrid/hooks/useValidBetInputValue';
import { useAutoCashoutSlice } from '../../store/slices/autocashout/selectors';
import { crashAutoplayActions } from '../../store/slices/crashAutoplay/slice';
import { useHighflyerStateSlice } from '../../store/slices/highflyerState/selectors';

import { BlockingModalContent } from './content/BlockingModalContent';
import { GameStore } from '../../store/GameStore';

export enum CrashPopupType {
  CrashAutoplayModified_1 = 'CrashAutoplayModified_1',
  CrashAutoplayModified_2 = 'CrashAutoplayModified_2',
}

export const CrashAutoplayModifiedConnected: FC<{
  gameStore: CoreStoreType;
  betBoardID: number;
}> = ({ gameStore, betBoardID }): ReactElement => {
  const dispatch = useCoreDispatch();
  const currentGameId = useGameStateSlice.gameId();
  const currentBet = useBetSlice.bets();
  const earlybets = useHighflyerStateSlice.getEarlyBetAmount(betBoardID);
  const { betInputAmount } = useValidBetInputValue(betBoardID);
  const totalBet = betInputAmount;
  const { crashAutoplay, bets } = gameStore.getState();
  const { isEnabled, cashoutMultiplier } = useAutoCashoutSlice.getAutoCashoutObj(betBoardID); // get bet id
  const isBetsOpen = useGameStateSlice.isBetsOpenStatus();

  useEffect(() => {
    Object.keys(bets?.betList || {})
      .map(Number)
      .forEach(() => {
        if (!crashAutoplay.isInProgress[betBoardID]) {
          dispatch(
            popupActions.dismissPopup(
              betBoardID == 1 ? CrashPopupType.CrashAutoplayModified_1 : CrashPopupType.CrashAutoplayModified_2
            )
          );
        }
      });
  }, [crashAutoplay.isInProgress[betBoardID], dispatch]);

  const action: BlockingModalActionButton[] = [
    {
      tk: 'THIS_ROUND_ONLY',
      type: ButtonVariant.MAIN,
      click: (): void => {
        Object.keys(bets?.betList || {})
          .map(Number)
          .forEach((sBet) => {
            const betId = bets.betList ? bets.betList[sBet] : 1;
            if (crashAutoplay.isInProgress[betId]) {
              dispatch(crashAutoplayActions.setIsThisRoundOnly({ bestSpot: betId, thisRoundonly: true }));
            }
          });
        dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
        dispatch(
          popupActions.dismissPopup(
            betBoardID == 1 ? CrashPopupType.CrashAutoplayModified_1 : CrashPopupType.CrashAutoplayModified_2
          )
        );
        Logger.logEvent('click', 'autoplay_modify', Date.now(), betBoardID, 'Popup_ThisRound', false);
      },
    },
    {
      tk: 'MODIFY',
      type: ButtonVariant.ACCENT,
      click: (): void => {
        if (currentBet) {
          const newBet: BetType = { betSpots: {} };
          newBet.betSpots[betBoardID] = isBetsOpen ? currentBet.betSpots[betBoardID] : { value: earlybets };
          dispatch(
            crashAutoplayActions.saveBet({
              bestSpot: betBoardID,
              savedBet: { betType: newBet, isEnable: isEnabled, value: cashoutMultiplier },
              gameId: currentGameId,
            })
          );
        }
        dispatch(
          popupActions.dismissPopup(
            betBoardID == 1 ? CrashPopupType.CrashAutoplayModified_1 : CrashPopupType.CrashAutoplayModified_2
          )
        );
        dispatch(toastActions.createToast('AUTOPLAY_BET_AMOUNT_MODIFIED'));
        Logger.logEvent('click', 'autoplay_modify', Date.now(), betBoardID, 'Popup_AllRounds', false);
      },
    },
  ];
  const messageContent = (
    <>
      {crashAutoplay.showModifiedPopUp[betBoardID].isBetChanged && (
        <>
          <L10nText tk="NEW_BET_AMOUNT" />
          &nbsp;
          <CurrencyConnected value={totalBet} />
          <br />
        </>
      )}
      {crashAutoplay.showModifiedPopUp[betBoardID].isAutoCashoutChanged && (
        <>
          <L10nText tk="SPM_AUTOPLAY_MODIFY100" />
          &nbsp;:&nbsp;
          {cashoutMultiplier}x
        </>
      )}
      {crashAutoplay.showModifiedPopUp[betBoardID].isNoChangesToShow && (
        <>
          <L10nText tk="SPM_AUTOPLAY_MODIFY100" />
          &nbsp;-
        </>
      )}

      <div style={{ paddingTop: '8px' }}>
        <L10nText tk="MODIFY_AUTOPLAY_WITH_NEW_BET" />
      </div>
    </>
  );

  return (
    <BlockingModalContent
      titleTk="MODIFY_AUTOPLAY"
      messageComponent={messageContent}
      actions={action}
      betCode={betBoardID}
    />
  );
};

export const crashautoplayModifiedConnectedOnClose = (dispatch: CoreDispatch): void => {
  const gameStore = GameStore;
  const { crashAutoplay, bets } = gameStore.getState();
  Object.keys(bets?.betList || {})
    .map(Number)
    .forEach((sBet) => {
      const betId = bets.betList ? bets.betList[sBet] : 1;
      if (crashAutoplay.isInProgress[betId]) {
        dispatch(crashAutoplayActions.setIsThisRoundOnly({ bestSpot: betId, thisRoundonly: true }));
      }
    });
  dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
};
